import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList } from "recharts";

const formatNumber = (value) => {
  if (value === null || value === undefined) return "0";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString("es-EC");
};

const formatDate = (value) => {
  if (!value) return "-";
  if (typeof value === "string") {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toISOString().slice(0, 10);
};

const accentMap = {
  blue: { bg: "#e0ecff", fg: "#1d4ed8" },
  violet: { bg: "#ebe9ff", fg: "#6d28d9" },
  green: { bg: "#e5f8f2", fg: "#0f766e" },
  amber: { bg: "#fff4e0", fg: "#c2410c" },
  aqua: { bg: "#e0f7ff", fg: "#0ea5e9" },
  coral: { bg: "#ffe7e2", fg: "#dc2626" },
};

export default function AcademicTab({ academico = {} }) {
  const kpis = academico.kpis || [];
  const ocupacion = academico.ocupacion || [];
  const proximas = academico.proximas || [];
  const hoy = new Date();

  const inferEstado = (p) => {
    if (p.estado) return (p.estado || "").toLowerCase();
    const inicio = p.fecha_inicio ? new Date(p.fecha_inicio) : null;
    if (!inicio || Number.isNaN(inicio.getTime())) return "cursando";
    return inicio > hoy ? "programada" : "cursando";
  };

  const ocupacionConEstado = ocupacion.map((p) => ({ ...p, estado: inferEstado(p) }));

  const ocupacionGraficable = ocupacionConEstado.filter((p) => {
    const matriculados = Number(p.matriculados) || 0;
    const esProgramadaOCurso = p.estado === "programada" || p.estado === "cursando";
    return esProgramadaOCurso && matriculados > 0;
  });

  const showChart = ocupacionGraficable.length > 0 && ocupacionGraficable.length <= 6;

  return (
    <>
      <div className="row g-3 admin-kpi-row">
        {kpis.map((kpi) => (
          <div className="col-12 col-sm-6 col-lg-4" key={kpi.label}>
            <div className="admin-kpi-card h-100">
              <div className="admin-kpi-body">
                <span
                  className="admin-kpi-icon"
                  style={{
                    backgroundColor: accentMap[kpi.accent]?.bg || "#e5e7eb",
                    color: accentMap[kpi.accent]?.fg || "#0f172a",
                  }}
                >
                  <i className={`bi ${kpi.icon || "bi-collection"} me-0`} />
                </span>
                <div className="admin-kpi-text">
                  <p className="admin-kpi-label mb-0">{kpi.label}</p>
                  <div className="admin-kpi-value">{formatNumber(kpi.value)}</div>
                  <div className="admin-kpi-helper">{kpi.helper}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mt-2">
        <div className="col-12 col-xl-6">
          <div className="card admin-section-card h-100">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">Ocupacion por Promocion</h6>
                <small className="text-muted">
                  Programadas / cursando con inscritos - {ocupacionGraficable.length}{" "}
                  {ocupacionGraficable.length === 1 ? "promocion" : "promociones"}
                </small>
              </div>
              <span className="dashboard-chip">
                <i className="bi bi-clipboard-check me-1" />
                {ocupacionGraficable.length}
              </span>
            </div>
            {showChart && (
              <div className="px-3 pb-2">
                <div className="small text-muted mb-1">
                  Grafico solo para programadas/cursando con inscritos (max 6). Eje en % de ocupacion.
                </div>
                <div style={{ minHeight: 260 }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={ocupacionGraficable} layout="vertical" margin={{ left: 12, right: 18, top: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} hide />
                      <YAxis type="category" dataKey="promocion" width={150} />
                      <Tooltip
                        formatter={(val, name, props) => {
                          const payload = props?.payload || {};
                          return [
                            `${val}%`,
                            `${payload.promocion || ""} (${formatNumber(payload.matriculados)}/${formatNumber(payload.capacidad)}) - ${
                              (payload.estado || "").charAt(0).toUpperCase() + (payload.estado || "").slice(1)
                            }`,
                          ];
                        }}
                        labelFormatter={() => ""}
                      />
                      <Bar dataKey="porcentaje" fill="#2563eb" radius={[6, 6, 6, 6]}>
                        <LabelList dataKey="porcentaje" position="right" formatter={(v) => `${v}%`} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Promocion</th>
                    <th>Inicio</th>
                    <th className="text-end">Capacidad</th>
                    <th className="text-end">Matriculados</th>
                    <th className="text-end">Ocupacion</th>
                  </tr>
                </thead>
                <tbody>
                  {ocupacionGraficable.length ? (
                    ocupacionGraficable.map((p) => (
                      <tr key={p.promocion}>
                        <td>{p.promocion}</td>
                        <td>{formatDate(p.fecha_inicio)}</td>
                        <td className="text-end">{formatNumber(p.capacidad)}</td>
                        <td className="text-end">{formatNumber(p.matriculados)}</td>
                        <td className="text-end">{p.porcentaje}%</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        Sin datos de ocupacion
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card admin-section-card h-100">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">Proximas Aperturas</h6>
                <small className="text-muted">Promociones futuras</small>
              </div>
              <span className="dashboard-chip">
                <i className="bi bi-calendar-event me-1" />
                {proximas.length}
              </span>
            </div>
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Promocion</th>
                    <th>Inicio</th>
                    <th className="text-end">Capacidad</th>
                    <th className="text-end">Matriculas</th>
                    <th className="text-end">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {proximas.length ? (
                    proximas.map((p) => {
                      const matriculas = p.matriculas ?? p.matriculados ?? 0;
                      const saldo = p.saldo ?? 0;
                      return (
                      <tr key={p.id || p.promocion}>
                        <td>{p.promocion}</td>
                        <td>{formatDate(p.fecha_inicio)}</td>
                        <td className="text-end">{formatNumber(p.capacidad)}</td>
                        <td className="text-end">{formatNumber(matriculas)}</td>
                        <td className="text-end">{formatNumber(saldo)}</td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        Sin proximas aperturas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
